const GA4_ANALYTICS_ENDPOINT = process.env.REACT_APP_GA4_ANALYTICS_ENDPOINT;

const emptyMetrics = {
  users: 0,
  sessions: 0,
  pageViews: 0,
  phoneClicks: 0,
  whatsappClicks: 0,
  bookingSubmissions: 0,
};

const formatDate = (date) => date.toISOString().split("T")[0];

const getStartOfToday = () => {
  const today = new Date();

  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

export const isGa4AnalyticsConfigured = () => Boolean(GA4_ANALYTICS_ENDPOINT);

export const getGa4DateRange = ({
  period,
  fromDate,
  selectedDay,
  toDate,
}) => {
  const today = getStartOfToday();
  const end = new Date(today);

  if (period === "today") {
    return {
      startDate: formatDate(today),
      endDate: formatDate(end),
      range: "today",
    };
  }

  if (period === "week") {
    const start = new Date(today);
    start.setDate(today.getDate() - 7);

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
      range: "week",
    };
  }

  if (period === "year") {
    const start = new Date(today.getFullYear(), 0, 1);

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
      range: "year",
    };
  }

  if (period === "single-day" && selectedDay) {
    return {
      startDate: selectedDay,
      endDate: selectedDay,
      range: "today",
    };
  }

  if (period === "custom" && fromDate && toDate) {
    return {
      startDate: fromDate,
      endDate: toDate,
      range: "custom",
    };
  }

  const start = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    startDate: formatDate(start),
    endDate: formatDate(end),
    range: "month",
  };
};

const toNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

const normalizeList = (items = [], nameKeys = ["name"], valueKeys = ["value"]) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      const nameKey = nameKeys.find((key) => item[key] !== undefined);
      const valueKey = valueKeys.find((key) => item[key] !== undefined);

      return {
        name: String(item[nameKey] || ""),
        value: toNumber(item[valueKey]),
        note: item.note || "",
        icon: item.icon,
      };
    })
    .filter((item) => item.name);
};

const normalizeGa4Analytics = (payload = {}) => {
  const metrics = payload.metrics || payload;

  return {
    metrics: {
      users: toNumber(metrics.users || metrics.activeUsers || metrics.totalUsers),
      sessions: toNumber(metrics.sessions),
      pageViews: toNumber(
        metrics.pageViews || metrics.screenPageViews || metrics.views
      ),
      phoneClicks: toNumber(
        metrics.phoneClicks || metrics.phone_click || metrics.phone_clicks
      ),
      whatsappClicks: toNumber(
        metrics.whatsappClicks ||
          metrics.whatsAppClicks ||
          metrics.whatsapp_click ||
          metrics.whatsapp_clicks
      ),
      bookingSubmissions: toNumber(
        metrics.bookingSubmissions ||
          metrics.booking_submit ||
          metrics.booking_submissions
      ),
    },
    topPages: normalizeList(
      payload.topPages || payload.pages,
      ["pagePath", "path", "page", "name"],
      ["views", "pageViews", "value"]
    ),
    devices: normalizeList(
      payload.devices || payload.sessionDevices,
      ["deviceCategory", "device", "name"],
      ["sessions", "users", "value"]
    ),
    channels: normalizeList(
      payload.channels || payload.sources || payload.topChannels,
      ["sessionSource", "source", "channel", "name"],
      ["sessions", "users", "value"]
    ),
    trend: Array.isArray(payload.trend) ? payload.trend : [],
  };
};

export const fetchGa4Analytics = async ({
  fromDate,
  period,
  selectedDay,
  toDate,
} = {}) => {
  if (!isGa4AnalyticsConfigured()) {
    return {
      configured: false,
      metrics: emptyMetrics,
      topPages: [],
      devices: [],
      channels: [],
      trend: [],
    };
  }

  const dateRange = getGa4DateRange({
    period,
    fromDate,
    selectedDay,
    toDate,
  });

  const response = await fetch(GA4_ANALYTICS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...dateRange,
      metrics: [
        "activeUsers",
        "sessions",
        "screenPageViews",
        "eventCount",
      ],
      events: ["phone_click", "whatsapp_click", "booking_submit"],
    }),
  });

  if (!response.ok) {
    throw new Error("GA4 analytics request failed.");
  }

  return {
    configured: true,
    ...normalizeGa4Analytics(await response.json()),
  };
};

export { emptyMetrics as emptyGa4Metrics };
